import React, { useEffect, useRef, useState, forwardRef } from "react";

/**
 * Mascote do logo. Reage como um bichinho:
 *   - mouse em cima: fecha os olhos de gostoso e balanca (carinho);
 *   - 1 toque: da um pulinho e mostra a lingua;
 *   - 2 toques seguidos: leva um susto;
 *   - 3 ou mais: fica BRAVO, solta fumacinha e treme.
 * Sozinho: pisca, olha para os lados e de vez em quando faz uma
 * careta (feliz, piscadinha, apaixonado, surpreso, lingua, estrelas,
 * dormindo, bravo). A antena acende e apaga sozinha.
 */

const VERDE = "#86efac";
const BRILHO = "#f0fdf4";

const olhoNormal = (cx, dx = 0, dy = 0) => (
  <g key={`n${cx}`}>
    <rect x={cx - 4 + dx} y={27.5 + dy} width="8" height="9" rx="3.5" fill={VERDE} />
    <rect x={cx - 1.5 + dx} y={29 + dy} width="2.6" height="2.6" rx="1.3" fill={BRILHO} />
  </g>
);
const olhoFechado = cx => (
  <rect key={`f${cx}`} x={cx - 4.5} y="31" width="9" height="2.6" rx="1.3" fill={VERDE} />
);
const olhoFeliz = cx => (
  <path key={`h${cx}`} d={`M${cx - 4.5} 33.5 Q${cx} 26.5 ${cx + 4.5} 33.5`}
    stroke={VERDE} strokeWidth="2.6" fill="none" strokeLinecap="round" />
);
const olhoCoracao = cx => (
  <path key={`c${cx}`} fill="#f472b6"
    d={`M${cx} 36.5 L${cx - 4.6} 31.6 A2.6 2.6 0 0 1 ${cx} 28.6 A2.6 2.6 0 0 1 ${cx + 4.6} 31.6 Z`} />
);
const olhoEstrela = cx => {
  const p = [];
  for (let i = 0; i < 10; i++) {
    const r = i % 2 === 0 ? 5 : 2.2;
    const a = (Math.PI / 5) * i - Math.PI / 2;
    p.push(`${(cx + r * Math.cos(a)).toFixed(2)},${(32 + r * Math.sin(a)).toFixed(2)}`);
  }
  return <polygon key={`e${cx}`} points={p.join(" ")} fill="#facc15" />;
};
const olhoSurpreso = cx => (
  <g key={`s${cx}`}>
    <circle cx={cx} cy="32" r="4.6" fill={VERDE} />
    <circle cx={cx} cy="32" r="1.8" fill="#052e16" />
  </g>
);
const olhoBravo = (cx, lado) => (
  <g key={`b${cx}`}>
    <rect x={cx - 4} y="29.5" width="8" height="7" rx="3" fill={VERDE} />
    <path d={lado < 0 ? `M${cx - 5} 26 L${cx + 4} 28.5` : `M${cx - 4} 28.5 L${cx + 5} 26`}
      stroke={VERDE} strokeWidth="2.2" strokeLinecap="round" />
  </g>
);

const bocaSorriso = <path d="M27 41.5 Q32 44.5 37 41.5" stroke={VERDE} strokeWidth="2.2" fill="none" strokeLinecap="round" />;
const bocaSorrisao = <path d="M26 40.5 Q32 46.5 38 40.5 Z" fill={VERDE} />;
const bocaO = <ellipse cx="32" cy="42.3" rx="2.4" ry="2.6" fill={VERDE} />;
const bocaReta = <path d="M28 42.5 L36 42.5" stroke={VERDE} strokeWidth="2.2" strokeLinecap="round" />;
const bocaLingua = (
  <g>
    <path d="M27 41 Q32 44 37 41" stroke={VERDE} strokeWidth="2.2" fill="none" strokeLinecap="round" />
    <path d="M30.5 42.6 Q32 47.5 33.5 42.6 Z" fill="#f472b6" />
  </g>
);

const ROSTOS = {
  normal: { olhos: [olhoNormal(24), olhoNormal(40)], boca: bocaSorriso },
  piscar: { olhos: [olhoFechado(24), olhoFechado(40)], boca: bocaSorriso },
  direita: { olhos: [olhoNormal(24, 2.5), olhoNormal(40, 2.5)], boca: bocaSorriso },
  esquerda: { olhos: [olhoNormal(24, -2.5), olhoNormal(40, -2.5)], boca: bocaSorriso },
  cima: { olhos: [olhoNormal(24, 0.5, -1.5), olhoNormal(40, 0.5, -1.5)], boca: bocaReta },
  feliz: { olhos: [olhoFeliz(24), olhoFeliz(40)], boca: bocaSorrisao },
  piscadinha: { olhos: [olhoNormal(24), olhoFeliz(40)], boca: bocaLingua },
  apaixonado: { olhos: [olhoCoracao(24), olhoCoracao(40)], boca: bocaSorrisao },
  surpreso: { olhos: [olhoSurpreso(24), olhoSurpreso(40)], boca: bocaO },
  lingua: { olhos: [olhoFeliz(24), olhoFeliz(40)], boca: bocaLingua },
  estrelas: { olhos: [olhoEstrela(24), olhoEstrela(40)], boca: bocaSorrisao },
  dormindo: { olhos: [olhoFechado(24), olhoFechado(40)], boca: bocaO, zzz: true },
  bravo: { olhos: [olhoBravo(24, -1), olhoBravo(40, 1)], boca: bocaReta },
  irritado: {
    olhos: [olhoBravo(24, -1), olhoBravo(40, 1)],
    boca: <path d="M27 43.5 Q32 39.5 37 43.5" stroke={VERDE} strokeWidth="2.2" fill="none" strokeLinecap="round" />,
    fumaca: true,
  },
  carinho: { olhos: [olhoFeliz(24), olhoFeliz(40)], boca: bocaSorriso, bochecha: true },
};

const CARETAS = ["feliz", "piscadinha", "apaixonado", "surpreso", "lingua", "estrelas", "dormindo", "bravo"];

// Um "ciclo" de vida: olha em volta, pisca e termina numa careta sorteada.
const montarCiclo = () => {
  const careta = CARETAS[Math.floor(Math.random() * CARETAS.length)];
  return [
    ["normal", 2200], ["piscar", 140], ["normal", 1600],
    ["direita", 900], ["esquerda", 900], ["normal", 700], ["piscar", 140],
    ["normal", 1200], ["cima", 700], ["normal", 500],
    [careta, careta === "dormindo" ? 2600 : 1800],
    ["piscar", 140],
  ];
};

const RoboAnimado = forwardRef(({ className, style, onClick, title }, ref) => {
  const [rosto, setRosto] = useState("normal");
  // Reacao ao toque/mouse: tem prioridade sobre o ciclo sozinho.
  const [reacao, setReacao] = useState(null); // { rosto, animacao }
  const toques = useRef({ n: 0, ultimo: 0 });
  const fimReacao = useRef(null);
  const emCima = useRef(false);

  useEffect(() => () => clearTimeout(fimReacao.current), []);

  const reagir = (r, ms) => {
    clearTimeout(fimReacao.current);
    setReacao(r);
    fimReacao.current = setTimeout(() => {
      setReacao(emCima.current ? { rosto: "carinho", animacao: "sbCarinho 1.4s ease-in-out infinite" } : null);
    }, ms);
  };

  const aoTocar = e => {
    const agora = Date.now();
    const t = toques.current;
    t.n = agora - t.ultimo < 2500 ? t.n + 1 : 1;
    t.ultimo = agora;
    if (t.n >= 3) {
      reagir({ rosto: "irritado", animacao: "sbTremer .5s ease-in-out 3" }, 2400);
    } else if (t.n === 2) {
      reagir({ rosto: "surpreso", animacao: "sbPular .5s ease-out" }, 1300);
    } else {
      reagir({ rosto: "piscadinha", animacao: "sbPular .5s ease-out" }, 1300);
    }
    if (onClick) onClick(e);
  };

  const aoEntrar = () => {
    emCima.current = true;
    if (!reacao) setReacao({ rosto: "carinho", animacao: "sbCarinho 1.4s ease-in-out infinite" });
  };
  const aoSair = () => {
    emCima.current = false;
    if (reacao && reacao.rosto === "carinho") setReacao(null);
  };

  useEffect(() => {
    let vivo = true;
    let timer = null;
    let ciclo = montarCiclo();
    let i = 0;
    const reduzir =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduzir) return undefined;

    const passo = () => {
      if (!vivo) return;
      if (i >= ciclo.length) {
        ciclo = montarCiclo();
        i = 0;
      }
      const [nome, ms] = ciclo[i++];
      setRosto(nome);
      timer = setTimeout(passo, ms);
    };
    passo();
    return () => {
      vivo = false;
      clearTimeout(timer);
    };
  }, []);

  const atual = reacao ? reacao.rosto : rosto;
  const r = ROSTOS[atual] || ROSTOS.normal;
  const estilo = reacao ? { ...(style || {}), animation: reacao.animacao } : style;

  return (
    <svg
      ref={ref}
      viewBox="0 0 64 64"
      className={className}
      style={estilo}
      onClick={aoTocar}
      onMouseEnter={aoEntrar}
      onMouseLeave={aoSair}
      role="img"
      aria-label="IA Consig"
    >
      {title ? <title>{title}</title> : null}
      <defs>
        <linearGradient id="roboCabeca" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#22c55e" />
          <stop offset="1" stopColor="#15803d" />
        </linearGradient>
      </defs>
      <circle cx="32" cy="7" r="7" fill="#4ade80" opacity=".35">
        <animate attributeName="opacity" values=".35;0;.35" dur="1.2s" repeatCount="indefinite" />
      </circle>
      <rect x="30" y="9" width="4" height="8" rx="2" fill="#166534" />
      <circle cx="32" cy="7" r="4.5" fill="#bbf7d0" stroke="#14532d" strokeWidth="1.5">
        <animate attributeName="fill" values="#bbf7d0;#15803d;#bbf7d0" dur="1.2s" repeatCount="indefinite" />
      </circle>
      <rect x="3" y="27" width="7" height="14" rx="3" fill="#166534" />
      <rect x="54" y="27" width="7" height="14" rx="3" fill="#166534" />
      <rect x="8" y="15" width="48" height="40" rx="14" fill="url(#roboCabeca)" stroke="#14532d" strokeWidth="2.5" />
      <rect x="14" y="22" width="36" height="25" rx="9" fill="#052e16" />
      {r.fumaca && (
        <g stroke="#f87171" strokeWidth="2.4" fill="none" strokeLinecap="round">
          <path d="M6 14 q3 -3 0 -6 q-3 -3 0 -6">
            <animate attributeName="opacity" values="1;.2;1" dur=".6s" repeatCount="indefinite" />
          </path>
          <path d="M58 14 q3 -3 0 -6 q-3 -3 0 -6">
            <animate attributeName="opacity" values=".2;1;.2" dur=".6s" repeatCount="indefinite" />
          </path>
        </g>
      )}
      {r.fumaca && <rect x="14" y="22" width="36" height="25" rx="9" fill="#ef4444" opacity=".18" />}
      {(atual === "apaixonado" || atual === "feliz" || r.bochecha) && (
        <g fill="#f472b6" opacity=".55">
          <ellipse cx="17.5" cy="41" rx="2.6" ry="1.6" />
          <ellipse cx="46.5" cy="41" rx="2.6" ry="1.6" />
        </g>
      )}
      {r.olhos}
      {r.boca}
      {r.zzz && (
        <text x="45" y="13" fontSize="11" fontWeight="800" fill="#4ade80" fontFamily="Arial, sans-serif">
          z
          <animate attributeName="opacity" values="0;1;0" dur="1.3s" repeatCount="indefinite" />
        </text>
      )}
    </svg>
  );
});

export default RoboAnimado;
